import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import { type CategorySectionFragmentFragment, graphql } from "@commerce-frontend/types";
import { Button } from "@commerce-frontend/ui/base/Button";
import { typography } from "@commerce-frontend/ui/base/Typography/typography";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@commerce-frontend/ui/components/Carousel/Carousel";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { Link } from "@commerce-frontend/ui/primitives/Link";
import { cva } from "class-variance-authority";
import { useTranslations } from "next-intl";
import type { HTMLAttributes } from "react";
import { CategoryCard } from "~/components/CategoryCard/CategoryCard";

graphql(/* GraphQL */ `
	fragment CategorySectionFragment on CategorySection {
		__typename
		id
		title
		category
		supportingText
		headerAlign
		variant
		link {
			url
			newTab
			hash
		}
		items {
			...CategoryCardFragment
		}
	}
`);

type Props = {
	data: CategorySectionFragmentFragment;
} & HTMLAttributes<HTMLDivElement>;

export const CategorySection = ({ data }: Props) => {
	const t = useTranslations("CategorySection");
	const { category, title, supportingText, link, items, headerAlign, variant } = data;

	return (
		<div
			className={cn(categorySectionVariants({ headerAlign }))}
			{...storyblokDataAttributes(data)}
		>
			<header className={cn(headerVariants({ headerAlign }))}>
				<div className="flex flex-col gap-md">
					<div className="flex flex-col gap-lg">
						{category && <div className="font-semibold text-brand-700">{category}</div>}
						<h2 className={typography("h2")}>{title}</h2>
					</div>
					{supportingText && <p className={typography("lead")}>{supportingText}</p>}
				</div>
				{link?.url && (
					<Button
						href={link.url}
						hash={link.hash ?? undefined}
						target={link.newTab ? "_blank" : undefined}
						className="self-end"
						variant="tertiaryColor"
					>
						{t("view-all")}
					</Button>
				)}
			</header>
			{variant === "grid" ? (
				<div className={cn("grid md:grid-cols-2 gap-4 lg:grid-cols-3")}>
					{items.map((item) =>
						item.cardLink?.url ? (
							<Link
								key={item?.id}
								href={item.cardLink?.url + (item.cardLink?.hash ? `#${item.cardLink?.hash}` : "")}
								target={item.cardLink?.newTab ? "_blank" : undefined}
							>
								<CategoryCard item={item} />
							</Link>
						) : (
							<CategoryCard key={item?.id} item={item} />
						),
					)}
				</div>
			) : (
				<Carousel
					className="grow overflow-hidden max-lg:-ml-[calc(1rem+2px)] max-lg:-mr-[calc(1rem)]"
					opts={{ slidesToScroll: "auto" }}
				>
					<div className="relative">
						<CarouselContent className="max-lg:px-container-sm">
							{items.map((item) => (
								<CarouselItem
									className="basis-11/12 min-[450px]:basis-96 min-[750px]:basis-80 min-[900px]:basis-[22rem]"
									key={item?.id}
								>
									{item.cardLink?.url ? (
										<Link
											key={item?.id}
											href={
												item.cardLink?.url + (item.cardLink?.hash ? `#${item.cardLink?.hash}` : "")
											}
											target={item.cardLink?.newTab ? "_blank" : undefined}
										>
											<CategoryCard item={item} />
										</Link>
									) : (
										<CategoryCard item={item} />
									)}
								</CarouselItem>
							))}
						</CarouselContent>
						<CarouselNext />
						<CarouselPrevious />
					</div>
				</Carousel>
			)}
		</div>
	);
};

const categorySectionVariants = cva("flex", {
	variants: {
		headerAlign: {
			top: "flex-col gap-4xl lg:gap-6xl",
			left: "flex-col gap-6xl lg:gap-4xl lg:flex-row",
		},
	},
	defaultVariants: {
		headerAlign: "top",
	},
});

const headerVariants = cva("flex flex-col gap-3xl", {
	variants: {
		headerAlign: {
			top: "justify-between lg:flex-row",
			left: "lg:w-[360px] lg:border-r-4 lg:border-gray-900 w-full lg:px-container lg:items-center lg:grow lg:shrink-0 lg:flex-col lg:justify-center",
		},
	},
	defaultVariants: {
		headerAlign: "top",
	},
});
